// TODO: Replace with the final URL before shipping.
export const CDC_ENVELOPE_DOCS_URL =
  'https://docs.nexla.com/user-guides/data-flows/data-flow-types/db-cdc';

// Shape A: single-table CDC — only nexla_op required at root.
const CDC_V1_SHAPE_A_PATHS: string[][] = [['nexla_op']];

// Shape B: multi-table CDC — nexla_cdc_info + nested row envelope required.
const CDC_V1_SHAPE_B_PATHS: string[][] = [
  ['nexla_op'],
  ['nexla_cdc_info'],
  ['nexla_op_table'],
  ['nexla_op_database'],
  ['nexla_cdc_info', 'before'],
  ['nexla_cdc_info', 'after'],
  ['nexla_cdc_info', 'primary_key'],
  ['nexla_cdc_info', 'schema'],
];

const V1_SPECS: Record<string, string[][]> = {
  // 'cdc' is not here — V1 CDC shape is resolved dynamically via detectCdcShape() before
  // this table is consulted, so it is never looked up here.
  // Covers ELT single-schema mode (singleSchemaMode = true), the expected config for UI-created flows.
  elt: [
    ['nexla_op_url'],
    ['nexla_op_object'],
    ['nexla_op_record'],
    ['nexla_op_metadata'],
    ['nexla_op_primary_keys'],
  ],
};

// V2 unified envelope — same spec for CDC and ELT.
// nexla_data.previous, nexla_data.schema, nexla_keys.partition omitted (nullable in spec).
const V2_UNIFIED_PATHS: string[][] = [
  ['nexla_version'],
  ['nexla_op'],
  ['nexla_source'],
  ['nexla_data'],
  ['nexla_keys'],
  ['nexla_config'],
  ['nexla_metadata'],
  ['nexla_source', 'type'],
  ['nexla_source', 'connection_type'],
  ['nexla_source', 'origin'],
  ['nexla_data', 'current'],
  ['nexla_keys', 'primary'],
];

const V2_SPECS: Record<string, string[][]> = {
  cdc: V2_UNIFIED_PATHS,
  elt: V2_UNIFIED_PATHS,
};

// V2 lenient: if the parent key exists but has no enumerated properties (backend returns
// { type: "object" } for Python dicts), treat nested paths as present. V1 is strict.
function isPathPresent(schema: any, path: string[], lenient = false): boolean {
  let node = schema;
  for (const key of path) {
    if (!node?.properties) return lenient;
    if (!node.properties[key]) return false;
    node = node.properties[key];
  }
  return true;
}

// Checks the flat sample field names (dot-notation keys from actual output data) as a fallback
// when the schema doesn't enumerate nested object properties.
function isPathInFlatSamples(path: string[], flatFieldNames: Set<string>): boolean {
  return flatFieldNames.has(path.join('.'));
}

/**
 * Returns 'B' when the parent nexset's input schema (fromSchema.byAttrId) contains
 * nexla_cdc_info — the CDC connector sets this for multi-table flows, making the input
 * the authoritative shape signal regardless of what the transform produces.
 * Returns 'A' otherwise (single-table CDC, or input schema not yet loaded).
 */
export function detectCdcShape(inputByAttrId: any): 'A' | 'B' {
  return inputByAttrId?.nexla_cdc_info ? 'B' : 'A';
}

/**
 * Returns dot-joined paths of required envelope keys absent from the output schema.
 * Returns [] when there is nothing to validate (no schema, unknown flow type, or empty spec).
 *
 * inputByAttrId: fromSchema.byAttrId from Redux — keyed by __name. Used to detect CDC
 * Shape B (multi-table) from the input, so the validator catches a dropped envelope even
 * when the transform omits nexla_cdc_info from its output entirely.
 *
 * flatFieldNames: dot-notation keys from toSamples.toFlat[*].attrs (actual output data).
 * Used as ground truth for nested paths when the backend emits { type: "object" } for
 * Python dicts and doesn't enumerate their properties in the schema.
 */
export function validateCdcEnvelope(
  outputSchema: any,
  flowType: string,
  useV2: boolean,
  flatFieldNames?: Set<string> | null,
  inputByAttrId?: Record<string, any> | null,
): string[] {
  if (!outputSchema?.properties) return [];

  let required: string[][];
  if (!useV2 && flowType === 'cdc') {
    const shape = detectCdcShape(inputByAttrId);
    required = shape === 'B' ? CDC_V1_SHAPE_B_PATHS : CDC_V1_SHAPE_A_PATHS;
  } else {
    const specs = useV2 ? V2_SPECS : V1_SPECS;
    required = specs[flowType];
  }

  if (!required) return [];
  const lenient = useV2;
  return required
    .filter((p) => {
      // For nested paths, flat samples are the ground truth when available — more reliable
      // than schema traversal because the backend may not enumerate nested object properties.
      // Top-level paths (length 1) always resolve cleanly from the schema.
      if (p.length > 1 && flatFieldNames?.size) {
        return !isPathInFlatSamples(p, flatFieldNames);
      }
      return !isPathPresent(outputSchema, p, lenient);
    })
    .map((p) => p.join('.'));
}
